import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiService } from '../services/apiService';
import { clearQuote } from '../utils/quoteStorage';

export interface UseReturningCustomerFlowReturn {
  showCardLinkingDialog: boolean;
  returningCustomerName: string;
  handleReturningCustomer: (isReturningCustomer: boolean, firstName: string, cardId: string, returnTo: string | null) => void;
  handleDialogOk: () => void;
  handleDialogCancel: () => void;
  createBanxaOrderFromQuote: (cardId: string, quote: any) => Promise<void>;
}

/**
 * Custom hook to handle returning customer flow logic
 * Manages welcome back popup, quote checking, and Banxa order creation
 */
export const useReturningCustomerFlow = (): UseReturningCustomerFlowReturn => {
  const navigate = useNavigate();
  const [showCardLinkingDialog, setShowCardLinkingDialog] = useState(false);
  const [returningCustomerName, setReturningCustomerName] = useState<string>('');
  const [currentCardId, setCurrentCardId] = useState<string>('');

  const createBanxaOrderFromQuote = useCallback(async (cardId: string, quote: any) => {
    try {
      // Validate quote data
      if (!quote || !quote.paymentMethodId || !quote.fiatCode || !quote.coinCode) {
        console.error('❌ [Returning Customer Flow] Invalid quote data:', quote);
        throw new Error('Invalid quote data: missing required fields');
      }

      const orderRequest = {
        cardId,
        fiatAmount: quote.fiatAmount,
        cryptoAmount: quote.cryptoAmount,
        fiatCode: quote.fiatCode,
        coinCode: quote.coinCode,
        paymentMethodId: quote.paymentMethodId,
        processingFee: quote.processingFee || 0,
        networkFee: quote.networkFee || 0,
        blockchain: quote.blockchain
      };

      const orderResponse = await ApiService.createQuoteOrder(orderRequest);

      // Verify response structure
      if (!orderResponse) {
        console.error('❌ [Returning Customer Flow] No response from API');
        throw new Error('No response from Banxa order creation API');
      }

      if (orderResponse.success && orderResponse.data?.checkoutUrl) {
        const checkoutUrl = orderResponse.data.checkoutUrl;
        
        // Validate checkout URL
        if (!checkoutUrl || typeof checkoutUrl !== 'string' || !checkoutUrl.startsWith('http')) {
          console.error('❌ [Returning Customer Flow] Invalid checkout URL:', checkoutUrl);
          throw new Error('Invalid checkout URL received from API');
        }

        clearQuote(cardId);
        window.location.href = checkoutUrl;
      } else {
        const errorMsg = orderResponse.error || 'Unknown error';
        console.error('❌ [Returning Customer Flow] Failed to create Banxa order');
        console.error('❌ [Returning Customer Flow] Error details:', errorMsg);
        console.error('❌ [Returning Customer Flow] Full response:', orderResponse);
        
        navigate('/purchase-crypto?cardId=' + cardId);
      }
    } catch (error) {
      console.error('❌ [Returning Customer Flow] Error creating Banxa order:', error);
      console.error('❌ [Returning Customer Flow] Error details:', error instanceof Error ? error.message : String(error));
      console.error('❌ [Returning Customer Flow] Error stack:', error instanceof Error ? error.stack : 'No stack trace');
      
      navigate('/purchase-crypto?cardId=' + cardId);
    }
  }, [navigate]);

  const navigateAfterReturningCustomerLink = useCallback(
    (cardId: string) => {
      navigate(`/card/${encodeURIComponent(cardId)}`);
    },
    [navigate]
  );

  const handleDialogOk = useCallback(async () => {
    if (!currentCardId) {
      console.error('❌ [Returning Customer Flow] No cardId available for card linking');
      return;
    }

    try {
      const result = await ApiService.addCardToUserByEmail(currentCardId);

      setShowCardLinkingDialog(false);
      if (result.success) {
        navigateAfterReturningCustomerLink(currentCardId);
      } else {
        console.error('❌ [Returning Customer Flow] Failed to link card:', result);
        navigateAfterReturningCustomerLink(currentCardId);
      }
    } catch (error) {
      console.error('❌ [Returning Customer Flow] Error linking card:', error);
      setShowCardLinkingDialog(false);
      navigateAfterReturningCustomerLink(currentCardId);
    }
  }, [currentCardId, navigateAfterReturningCustomerLink]);

  const handleDialogCancel = useCallback(() => {
    setShowCardLinkingDialog(false);
    if (currentCardId) {
      navigate(`/welcome?cardId=${currentCardId}`);
    } else {
      navigate('/welcome');
    }
  }, [currentCardId, navigate]);

  const handleReturningCustomer = useCallback(
    (isReturningCustomer: boolean, firstName: string, cardId: string, returnTo: string | null) => {
      if (!isReturningCustomer) {
        return;
      }

      // Store current cardId for dialog handlers
      setCurrentCardId(cardId);

      setReturningCustomerName(firstName || '');
      setShowCardLinkingDialog(true);
    },
    []
  );

  return {
    showCardLinkingDialog,
    returningCustomerName,
    handleReturningCustomer,
    handleDialogOk,
    handleDialogCancel,
    createBanxaOrderFromQuote
  };
};

